import { useEffect, useMemo, useState } from 'react'

import { type Payouts } from '@airwallex/components-sdk'
import { useSelector } from 'react-redux'

import { toast } from '@webapp/components/Notification'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useFinancialAccountState, useProcurifyIntl } from '@webapp/hooks'
import { FinancialAccountTypes } from '@webapp/models/enums'
import { type Vendor } from '@webapp/modules/vendor/entities/Vendor'
import { SelectPaymentMethodCategoryStep } from './components/SelectPaymentMethodCategoryStep'
import { type OnSubmitPayload as OnSubmitSelectPaymentMethodCategoryPayload } from './hooks/useSelectPaymentMethodCategoryStep'
import messages from './messages'
import { CreatePaymentMethodFormStyled } from './styles/CreatePaymentMethodFormStyled'
import { formatAirwallexBeneficiaryForSubmissionUseCase } from '../../business-logic/use-cases/FormatAirwallexBeneficiaryForSubmissionUseCase'
// TODO: layer violation — presentation layer must not reach data-access/repositories; move this into a Model
// eslint-disable-next-line @procurify/frontend-architecture-rules/layer-no-data-access-import
import { paymentMethodRepository } from '../../data-access/repositories/PaymentMethodRepository'
import {
  PaymentMethodCategoryTypes,
  type PaymentMethodCategoryType,
} from '../../entities/enums/PaymentMethodCategoryEnum'
import { type PaymentMethodConstructor } from '../../entities/PaymentMethod'
import { CreateBeneficiaryFormPresenter } from '../../features/create-beneficiary/presenters/CreateBeneficiaryPresenter/CreateBeneficiaryFormPresenter'
import { CreateExternalPaymentMethodFormPresenter } from '../../features/create-external-payment-method/presenters/CreateExternalPaymentMethodFormPresenter'

export interface ICreatePaymentMethodFormProps {
  open: boolean
  vendorId: Vendor['id']
  onClose: () => void
  onAirwallexPaymentMethodCreateSuccess: (
    paymentMethod: PaymentMethodConstructor
  ) => void
  onExternalPaymentMethodCreateSuccess: (
    paymentMethod: PaymentMethodConstructor
  ) => void
  initialPaymentMethodCategory?: PaymentMethodCategoryType
}

enum CreateBeneficiaryFormStep {
  TYPE_SELECT = 1,
  BENEFICIARY_FORM = 2,
  EXTERNAL_PAYMENT_METHOD_FORM = 3,
}

export const CreatePaymentMethodForm = ({
  open,
  vendorId,
  onClose,
  onAirwallexPaymentMethodCreateSuccess,
  onExternalPaymentMethodCreateSuccess,
  initialPaymentMethodCategory,
}: ICreatePaymentMethodFormProps) => {
  const intl = useProcurifyIntl()

  const [selectedPaymentMethodCategory, setSelectedPaymentMethodCategory] =
    useState<PaymentMethodCategoryType | null>(null)

  const [isDirectExternalForm, setIsDirectExternalForm] = useState(false)

  const { bill_pay } = useSelector(makeSelectFeatureSwitches())
  const {
    isFinancialAccountSetUp,
    financialAccountType,
    isLoadingFinancialAccountSetupState,
  } = useFinancialAccountState({ enabled: bill_pay && open })

  const currentStep = useMemo(() => {
    if (isLoadingFinancialAccountSetupState) {
      return null
    }
    if (selectedPaymentMethodCategory === null) {
      return CreateBeneficiaryFormStep.TYPE_SELECT
    }
    if (selectedPaymentMethodCategory === PaymentMethodCategoryTypes.EXTERNAL) {
      return CreateBeneficiaryFormStep.EXTERNAL_PAYMENT_METHOD_FORM
    }
    return CreateBeneficiaryFormStep.BENEFICIARY_FORM
  }, [selectedPaymentMethodCategory, isLoadingFinancialAccountSetupState])

  useEffect(() => {
    if (!open) {
      setSelectedPaymentMethodCategory(null)
      setIsDirectExternalForm(false)
    }
  }, [open])

  useEffect(() => {
    if (
      selectedPaymentMethodCategory === null &&
      !isLoadingFinancialAccountSetupState
    ) {
      const shouldSkipToExternalForm =
        !bill_pay ||
        initialPaymentMethodCategory === PaymentMethodCategoryTypes.EXTERNAL ||
        !isFinancialAccountSetUp ||
        (financialAccountType !== FinancialAccountTypes.AIRWALLEX_CAD &&
          financialAccountType !== FinancialAccountTypes.AIRWALLEX_USD)

      setSelectedPaymentMethodCategory(
        shouldSkipToExternalForm
          ? PaymentMethodCategoryTypes.EXTERNAL
          : (initialPaymentMethodCategory ?? null)
      )
      setIsDirectExternalForm(shouldSkipToExternalForm)
    }
  }, [
    bill_pay,
    isFinancialAccountSetUp,
    initialPaymentMethodCategory,
    financialAccountType,
    selectedPaymentMethodCategory,
    isLoadingFinancialAccountSetupState,
  ])

  const handleTypeSelectSubmit = (
    data: OnSubmitSelectPaymentMethodCategoryPayload
  ) => {
    const { paymentMethodCategoryType } = data
    setSelectedPaymentMethodCategory(paymentMethodCategoryType)
  }

  const handleBackToTypeSelect = () => {
    setSelectedPaymentMethodCategory(null)
  }

  const handleBeneficiaryFormSubmit = async (
    beneficiaryFormSubmitResponse: Payouts.BeneficiaryFormSubmitResponse
  ) => {
    if (!beneficiaryFormSubmitResponse) {
      return
    }

    try {
      const formattedPayload = formatAirwallexBeneficiaryForSubmissionUseCase(
        vendorId,
        beneficiaryFormSubmitResponse
      )

      const response =
        await paymentMethodRepository.createAirwallexPaymentMethod(
          formattedPayload
        )

      if (response?.data) {
        onAirwallexPaymentMethodCreateSuccess(response.data)
      }

      toast.success({
        title: intl.formatMessage(
          messages.airwallexPaymentMethodCreatedSuccessMessage
        ),
      })

      onClose()
    } catch (error) {
      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children:
          error instanceof Error
            ? error.message
            : intl.formatMessage(
                messages.createAirwallexPaymentMethodErrorMessage
              ),
      })
    }
  }

  const handleExternalPaymentMethodCreateSuccess = (
    paymentMethod: PaymentMethodConstructor
  ) => {
    onExternalPaymentMethodCreateSuccess(paymentMethod)

    onClose()
  }

  const isExternalPaymentMethod =
    selectedPaymentMethodCategory === PaymentMethodCategoryTypes.EXTERNAL

  const handleOnExternalPaymentMethodCancel = () => {
    if (initialPaymentMethodCategory && isExternalPaymentMethod) {
      onClose()
    } else {
      handleBackToTypeSelect()
    }
  }

  if (!open) {
    return null
  }

  return (
    <CreatePaymentMethodFormStyled>
      {currentStep === CreateBeneficiaryFormStep.TYPE_SELECT && (
        <SelectPaymentMethodCategoryStep
          isFinancialAccountSetUp={isFinancialAccountSetUp}
          financialAccountType={financialAccountType}
          onSubmit={handleTypeSelectSubmit}
          onCancel={onClose}
        />
      )}

      {currentStep === CreateBeneficiaryFormStep.BENEFICIARY_FORM &&
        selectedPaymentMethodCategory &&
        !isExternalPaymentMethod && (
          <CreateBeneficiaryFormPresenter
            paymentMethodCategory={
              selectedPaymentMethodCategory as Exclude<
                PaymentMethodCategoryType,
                | typeof PaymentMethodCategoryTypes.EXTERNAL
                | typeof PaymentMethodCategoryTypes.INTERNATIONAL_US
              >
            }
            onSubmit={handleBeneficiaryFormSubmit}
            onBack={handleBackToTypeSelect}
          />
        )}

      {currentStep === CreateBeneficiaryFormStep.EXTERNAL_PAYMENT_METHOD_FORM &&
        isExternalPaymentMethod && (
          <CreateExternalPaymentMethodFormPresenter
            vendorId={vendorId}
            onCancel={handleOnExternalPaymentMethodCancel}
            onSuccess={handleExternalPaymentMethodCreateSuccess}
            shouldShowCancelButtonLabel={isDirectExternalForm}
          />
        )}
    </CreatePaymentMethodFormStyled>
  )
}
